import React, { useEffect, useState } from "react";
import { Button, Col, Empty, Input, message, Modal, PageHeader, Result, Row, Space, Switch, Table, Tag, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { CopyOutlined, DeleteOutlined, KeyOutlined, PlusOutlined, StopOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { ApiKey, ApiKeyCreateRequest, ApiKeyCreateResponse } from "../data/ApiKey";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

const ApiKeys: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [apiKeys, setApiKeys] = useState<Array<ApiKey>>([]);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [newKeyName, setNewKeyName] = useState("");
    const [canManageAssets, setCanManageAssets] = useState(true);
    const [createdKey, setCreatedKey] = useState<ApiKeyCreateResponse | null>(null);
    const [showKeyModalVisible, setShowKeyModalVisible] = useState(false);
    const [creating, setCreating] = useState(false);

    const fetchApiKeys = async () => {
        setLoading(true);
        setHasError(false);

        try {
            const response = await fetch(`/api/ApiKeys`, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-type": "application/json"
                }
            });

            if (response.status !== 200) {
                setLoading(false);
                setHasError(true);
                return;
            }

            const data = await response.json();
            setApiKeys(data);
        } catch (error) {
            setHasError(true);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchApiKeys();
    }, []);

    const handleCreateApiKey = async () => {
        if (!newKeyName.trim()) {
            message.error("Please enter a name for the API key");
            return;
        }

        setCreating(true);

        try {
            const response = await fetch(`/api/ApiKeys`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-type": "application/json"
                },
                body: JSON.stringify({ name: newKeyName, canManageAssets } as ApiKeyCreateRequest)
            });

            if (response.status !== 200) {
                message.error("Failed to create API key");
                setCreating(false);
                return;
            }

            const data: ApiKeyCreateResponse = await response.json();
            setCreatedKey(data);
            setCreateModalVisible(false);
            setShowKeyModalVisible(true);
            setNewKeyName("");
            setCanManageAssets(true);
            await fetchApiKeys();
        } catch (error) {
            message.error("Failed to create API key");
        }

        setCreating(false);
    };

    const handleDeleteApiKey = async (id: number, name: string) => {
        Modal.confirm({
            title: "Delete API Key",
            content: `Are you sure you want to delete the API key "${name}"? This action cannot be undone.`,
            okText: "Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    const response = await fetch(`/api/ApiKeys/${id}`, {
                        method: "DELETE"
                    });

                    if (response.status === 204) {
                        message.success("API key deleted successfully");
                        await fetchApiKeys();
                    } else {
                        message.error("Failed to delete API key");
                    }
                } catch (error) {
                    message.error("Failed to delete API key");
                }
            }
        });
    };

    const handleToggleApiKey = async (id: number) => {
        try {
            const response = await fetch(`/api/ApiKeys/${id}/toggle`, {
                method: "PATCH",
                headers: {
                    Accept: "application/json"
                }
            });

            if (response.status === 200) {
                message.success("API key status updated");
                await fetchApiKeys();
            } else {
                message.error("Failed to update API key status");
            }
        } catch (error) {
            message.error("Failed to update API key status");
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        message.success("Copied to clipboard");
    };

    const columns: ColumnsType<ApiKey> = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text: string) => <Text strong>{text}</Text>
        },
        {
            title: "Key Prefix",
            dataIndex: "keyPrefix",
            key: "keyPrefix",
            render: (text: string) => (
                <Text code>
                    {text}
                    {"*".repeat(32)}
                </Text>
            )
        },
        {
            title: "Asset Management",
            dataIndex: "canManageAssets",
            key: "canManageAssets",
            render: (value: boolean) => (value ? <Tag color="blue">Enabled</Tag> : <Tag>Disabled</Tag>)
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive: boolean) => (isActive ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>)
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date: string) => <Tooltip title={dayjs(date).format("YYYY-MM-DD HH:mm:ss")}>{dayjs(date).fromNow()}</Tooltip>
        },
        {
            title: "Last Used",
            dataIndex: "lastUsedAt",
            key: "lastUsedAt",
            render: (date: string | null) =>
                date ? <Tooltip title={dayjs(date).format("YYYY-MM-DD HH:mm:ss")}>{dayjs(date).fromNow()}</Tooltip> : <Text type="secondary">Never</Text>
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record: ApiKey) => (
                <Space>
                    <Tooltip title={record.isActive ? "Deactivate" : "Activate"}>
                        <Switch
                            checked={record.isActive}
                            onChange={() => handleToggleApiKey(record.id)}
                            checkedChildren={<CheckCircleOutlined />}
                            unCheckedChildren={<StopOutlined />}
                        />
                    </Tooltip>
                    <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteApiKey(record.id, record.name)}>
                        Delete
                    </Button>
                </Space>
            )
        }
    ];

    if (hasError) {
        return <Result status="500" title="500" subTitle="Sorry, something went wrong. Please try again later." />;
    }

    return (
        <div>
            <PageHeader
                title="API Keys"
                subTitle="Manage API keys for accessing the Meteogen API"
                extra={[
                    <Button key="create" type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                        Create API Key
                    </Button>
                ]}
            />

            {apiKeys.length === 0 && !loading ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No API keys yet">
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                        Create your first API key
                    </Button>
                </Empty>
            ) : (
                <Table loading={loading} columns={columns} dataSource={apiKeys} rowKey="id" pagination={{ pageSize: 10 }} />
            )}

            <Modal
                title="Create New API Key"
                visible={createModalVisible}
                onOk={handleCreateApiKey}
                onCancel={() => {
                    setCreateModalVisible(false);
                    setNewKeyName("");
                    setCanManageAssets(true);
                }}
                confirmLoading={creating}
                okText="Create"
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Text>Enter a descriptive name for this API key:</Text>
                    <Input
                        placeholder="e.g., Production Server, Mobile App, etc."
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        onPressEnter={handleCreateApiKey}
                        prefix={<KeyOutlined />}
                    />
                    <Space>
                        <Switch checked={canManageAssets} onChange={setCanManageAssets} />
                        <Text>Allow this key to manage assets (create/update/delete)</Text>
                    </Space>
                </Space>
            </Modal>

            <Modal
                title="API Key Created Successfully"
                visible={showKeyModalVisible}
                onOk={() => {
                    setShowKeyModalVisible(false);
                    setCreatedKey(null);
                }}
                onCancel={() => {
                    setShowKeyModalVisible(false);
                    setCreatedKey(null);
                }}
                footer={[
                    <Button
                        key="close"
                        type="primary"
                        onClick={() => {
                            setShowKeyModalVisible(false);
                            setCreatedKey(null);
                        }}
                    >
                        I've saved the key
                    </Button>
                ]}
            >
                {createdKey && (
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Text type="warning" strong>
                            Important: Copy this API key now. You won't be able to see it again!
                        </Text>
                        <Paragraph>
                            <Text strong>Name:</Text> {createdKey.name}
                        </Paragraph>
                        <Paragraph>
                            <Text strong>Asset management:</Text> {createdKey.canManageAssets ? "Enabled" : "Disabled"}
                        </Paragraph>
                        <Paragraph>
                            <Text strong>API Key:</Text>
                        </Paragraph>
                        <Row gutter={8}>
                            <Col flex="auto">
                                <Input.TextArea value={createdKey.apiKey} autoSize readOnly style={{ fontFamily: "monospace" }} />
                            </Col>
                            <Col>
                                <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(createdKey.apiKey)}>
                                    Copy
                                </Button>
                            </Col>
                        </Row>
                        <Text type="secondary">Use this key in the X-API-Key header when making API requests.</Text>
                    </Space>
                )}
            </Modal>
        </div>
    );
};

export default ApiKeys;
